/* eslint-disable i18next/no-literal-string */
/**
 *
 * Renders a single `<script type="application/ld+json">` block containing a
 * `@graph` array with three or four nodes:
 *
 *   1. CollectionPage  (always)
 *   2. BreadcrumbList  (always; current page has no `item` URL per Schema.org)
 *   3. ItemList        (always; up to MAX_ITEM_LIST_PRODUCTS products)
 *   4. FAQPage         (only when faqs are present)
 *
 * The CollectionPage node gains a `significantLink` array (CMS links +
 * subcollection URLs) whenever the builder supplies any.
 *
 * The component is intentionally path-agnostic: it accepts pre-normalized
 * props that the PL extractor produces.
 */
import type {
  CollectionPageSchemaBreadcrumb,
  CollectionPageSchemaFaq,
  CollectionPageSchemaProduct,
  CollectionPageSchemaProps,
} from '@src/server-side/page-data/pl/page-type-handlers/collection-page-schema-data-pl'
import { getNonce } from '@src/utils/nonce.utils'

const id = (pageUrl: string, fragment: string): string => `${pageUrl}#${fragment}`

interface BreadcrumbListItemNode {
  '@type': 'ListItem'
  position: number
  name: string
  item?: string
}

interface ProductListItemNode {
  '@type': 'ListItem'
  position: number
  name: string
  url: string
}

const buildBreadcrumbListItem = (
  breadcrumb: CollectionPageSchemaBreadcrumb,
  index: number,
): BreadcrumbListItemNode => ({
  '@type': 'ListItem',
  position: index + 1,
  name: breadcrumb.name,
  ...(breadcrumb.url ? { item: breadcrumb.url } : {}),
})

const buildProductListItem = (product: CollectionPageSchemaProduct, index: number): ProductListItemNode => ({
  '@type': 'ListItem',
  position: index + 1,
  name: product.name,
  url: product.url,
})

interface FaqQuestionNode {
  '@type': 'Question'
  name: string
  acceptedAnswer: { '@type': 'Answer'; text: string }
}

const buildFaqQuestionNode = (faq: CollectionPageSchemaFaq): FaqQuestionNode => ({
  '@type': 'Question',
  name: faq.question,
  acceptedAnswer: { '@type': 'Answer', text: faq.answer },
})

export const CollectionPageSchema = (props: CollectionPageSchemaProps): JSX.Element => {
  const { pageUrl, pageName, description, aboutName, numberOfItems, products, breadcrumbs, faqs, significantLinks } =
    props

  // FAQ presence gates only the FAQPage node + its hasPart reference. significantLink is
  // independent — it renders whenever the builder supplies links (CMS + subcollection URLs).
  const hasFaqs = faqs.length > 0
  const hasBreadcrumbs = breadcrumbs.length > 0

  const collectionPageNode: Record<string, unknown> = {
    '@type': 'CollectionPage',
    '@id': id(pageUrl, 'webpage'),
    url: pageUrl,
    name: pageName,
    description,
    about: { '@type': 'Thing', name: aboutName },
    ...(hasBreadcrumbs ? { breadcrumb: { '@id': id(pageUrl, 'breadcrumb') } } : {}),
    mainEntity: { '@id': id(pageUrl, 'itemlist') },
    ...(hasFaqs ? { hasPart: { '@id': id(pageUrl, 'faq') } } : {}),
    ...(significantLinks.length > 0 ? { significantLink: significantLinks } : {}),
  }

  const graph: Record<string, unknown>[] = [collectionPageNode]

  if (hasBreadcrumbs) {
    graph.push({
      '@type': 'BreadcrumbList',
      '@id': id(pageUrl, 'breadcrumb'),
      itemListElement: breadcrumbs.map(buildBreadcrumbListItem),
    })
  }

  graph.push({
    '@type': 'ItemList',
    '@id': id(pageUrl, 'itemlist'),
    numberOfItems,
    itemListElement: products.map(buildProductListItem),
  })

  if (hasFaqs) {
    graph.push({
      '@type': 'FAQPage',
      '@id': id(pageUrl, 'faq'),
      mainEntity: faqs.map(buildFaqQuestionNode),
    })
  }

  const payload = {
    '@context': 'https://schema.org',
    '@graph': graph,
  }

  return (
    <script
      type="application/ld+json"
      nonce={getNonce() ?? undefined}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(payload).replace(/</g, '\\u003C') }}
    />
  )
}
/* eslint-enable i18next/no-literal-string */
